import { useState, useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';
import { useSketchStore } from '../../state/useSketchStore';
import { notifyCameraChanged } from '../../rendering/cameraTick';
import { registerSketchTool, type SketchHit } from './sketchToolBus';

/**
 * Camera / navigation tools (SketchUp parity): Orbit, Pan, Zoom, Zoom
 * Window, Zoom Extents, Position Camera, Look Around and Walk.
 *
 * Orbit and Pan re-map the LEFT mouse button on the shared OrbitControls
 * while active; the others drive the camera manually through drag gestures
 * (bus pointer-down + window-level move/up listeners so drags keep working
 * outside the canvas) and explicitly invalidate the demand-render loop.
 */

interface OrbitControlsLike {
    target: THREE.Vector3;
    mouseButtons: { LEFT?: THREE.MOUSE; MIDDLE?: THREE.MOUSE; RIGHT?: THREE.MOUSE };
    update: () => void;
}

const EYE_HEIGHT = 1.7;

const targetOf = (controls: OrbitControlsLike | null): THREE.Vector3 =>
    controls ? controls.target : new THREE.Vector3();

/** Suppress the default select/clear contract while a camera drag owns the pointer. */
function useCameraBus(handlers?: { onDown?: (hit: SketchHit) => void }): void {
    useEffect(() => {
        registerSketchTool({ onDown: handlers?.onDown ?? (() => {}), onMove: () => {} });
        return () => registerSketchTool(null);
    });
}

/** Left button remap for Orbit/Pan (restored on unmount). */
function useLeftButton(mode: THREE.MOUSE | undefined): void {
    const controls = useThree((s) => s.controls) as OrbitControlsLike | null;
    useEffect(() => {
        if (!controls) return;
        const previous = controls.mouseButtons.LEFT;
        controls.mouseButtons.LEFT = mode;
        return () => {
            controls.mouseButtons.LEFT = previous;
        };
    }, [controls, mode]);
}

/** Escape returns to Select, like Sketch's camera tools. Also shared by
 *  the UCS tools (SketchUcsTools), which hand back the same way. */
export function useEscapeToSelect(): void {
    useEffect(() => {
        const onKey = (e: KeyboardEvent): void => {
            if (e.key !== 'Escape') return;
            import('../../state/store').then(({ useAppStore }) => {
                useAppStore.getState().setActiveTool('select');
            });
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);
}

/** Common camera plumbing for drag-driven tools. */
function useCameraRig() {
    const camera = useThree((s) => s.camera);
    const controls = useThree((s) => s.controls) as OrbitControlsLike | null;
    const gl = useThree((s) => s.gl);

    const sync = useCallback((lookPoint?: THREE.Vector3) => {
        camera.updateProjectionMatrix();
        if (controls) {
            if (lookPoint) controls.target.copy(lookPoint);
            controls.update();
        } else if (lookPoint) {
            camera.lookAt(lookPoint);
        }
        // Demand render loop: manual camera moves must schedule a frame.
        requestFrame();
        notifyCameraChanged();
    }, [camera, controls]);

    /** Unproject canvas px onto the view plane through `anchor`. */
    const unprojectToViewPlane = useCallback((px: THREE.Vector2, anchor: THREE.Vector3): THREE.Vector3 | null => {
        const rect = gl.domElement.getBoundingClientRect();
        const ndc = new THREE.Vector2(
            (px.x / Math.max(rect.width, 1)) * 2 - 1,
            -(px.y / Math.max(rect.height, 1)) * 2 + 1
        );
        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(ndc, camera);
        const normal = camera.getWorldDirection(new THREE.Vector3()).negate();
        const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(normal, anchor);
        const out = new THREE.Vector3();
        return raycaster.ray.intersectPlane(plane, out) ? out : null;
    }, [camera, gl]);

    const localPx = useCallback((clientX: number, clientY: number): THREE.Vector2 => {
        const rect = gl.domElement.getBoundingClientRect();
        return new THREE.Vector2(clientX - rect.left, clientY - rect.top);
    }, [gl]);

    return { camera, controls, gl, sync, unprojectToViewPlane, localPx };
}

/** R3F invalidate bridge: camera tools request frames through it. */
let invalidateHook: (() => void) | null = null;

/** Register the canvas invalidate fn so camera tools can schedule frames. */
export function registerSketchInvalidate(fn: (() => void) | null): void {
    invalidateHook = fn;
}

export function SketchInvalidateBridge(): null {
    const invalidate = useThree((s) => s.invalidate);
    useEffect(() => {
        registerSketchInvalidate(invalidate);
        return () => registerSketchInvalidate(null);
    }, [invalidate]);
    return null;
}

const requestFrame = (): void => { invalidateHook?.(); };

/**
 * Frame a world box into the view (mirrors ZoomChordHandler's math for the
 * sketch workbench, keeping the current view direction).
 */
function fitBox(
    camera: THREE.Camera,
    controls: OrbitControlsLike | null,
    box: THREE.Box3 | null,
    keepDirection: boolean
): void {
    const center = box ? box.getCenter(new THREE.Vector3()) : new THREE.Vector3();
    const radius = box ? Math.max(box.getSize(new THREE.Vector3()).length() / 2, 0.5) : 10;
    if ((camera as THREE.OrthographicCamera).isOrthographicCamera) {
        const ortho = camera as THREE.OrthographicCamera;
        const viewH = Math.max(ortho.top - ortho.bottom, 1);
        ortho.zoom = Math.max(viewH / (radius * 2.4), 0.01);
        ortho.updateProjectionMatrix();
    } else {
        const fov = (camera as THREE.PerspectiveCamera).fov ?? 50;
        const distance = radius / Math.sin(THREE.MathUtils.degToRad(fov) / 2);
        const direction = keepDirection
            ? new THREE.Vector3().subVectors(camera.position, targetOf(controls)).normalize()
            : new THREE.Vector3(1, 1, 1).normalize();
        camera.position.copy(center).addScaledVector(direction, distance);
    }
    camera.up.set(0, 0, 1);
    if (controls) {
        controls.target.copy(center);
        controls.update();
    } else {
        camera.lookAt(center);
    }
    // Both concrete camera classes expose updateProjectionMatrix; the shared
    // THREE.Camera parameter type does not, so cast like the branches above.
    (camera as THREE.PerspectiveCamera).updateProjectionMatrix();
    notifyCameraChanged();
}

/** World bounds of the sketch document (solids + loose edges + guides). */
export function sketchDocumentBounds(): THREE.Box3 | null {
    const state = useSketchStore.getState();
    const box = new THREE.Box3();
    let found = false;
    Object.values(state.solids).forEach((solid) => {
        if (!solid.visible) return;
        solid.faces.forEach((face) => {
            face.outer.forEach((p) => {
                box.expandByPoint(new THREE.Vector3(...p));
                found = true;
            });
        });
    });
    Object.values(state.edges).forEach((edge) => {
        box.expandByPoint(new THREE.Vector3(...edge.a));
        box.expandByPoint(new THREE.Vector3(...edge.b));
        found = true;
    });
    return found ? box : null;
}

/**
 * World bounds of the SKETCH selection (Zoom Selection): face composites
 * bound just those faces, solid ids the whole solid, guides their geometry.
 * Null when the selection carries no boundable geometry.
 */
export function sketchSelectionBounds(): THREE.Box3 | null {
    const state = useSketchStore.getState();
    const box = new THREE.Box3();
    let found = false;
    const expandLoop = (loop: readonly (readonly [number, number, number])[]): void => {
        loop.forEach((p) => {
            box.expandByPoint(new THREE.Vector3(...p));
            found = true;
        });
    };
    for (const id of state.selectedIds) {
        if (id.startsWith('guide:')) {
            const guide = state.guides[id.slice('guide:'.length)];
            if (guide) {
                expandLoop([guide.a]);
                if (guide.b) expandLoop([guide.b]);
            }
            continue;
        }
        const idx = id.indexOf(':');
        if (idx > 0) {
            const solid = state.solids[id.slice(0, idx)];
            const face = solid?.faces.find((f) => f.id === id.slice(idx + 1));
            if (face) {
                expandLoop(face.outer);
                face.holes.forEach(expandLoop);
            }
            continue;
        }
        const solid = state.solids[id];
        if (solid) solid.faces.forEach((face) => expandLoop(face.outer));
    }
    return found ? box : null;
}

// ============================== ORBIT ==============================

export function useSketchOrbitTool(): void {
    useCameraBus();
    useLeftButton(THREE.MOUSE.ROTATE);
    useEscapeToSelect();
}

// ============================== PAN ==============================

export function useSketchPanTool(): void {
    useCameraBus();
    useLeftButton(THREE.MOUSE.PAN);
    useEscapeToSelect();
}

// ============================== ZOOM (drag dolly) ==============================

export function useSketchZoomTool(): void {
    useEscapeToSelect();
    const { camera, controls, sync } = useCameraRig();
    const rigRef = useRef({ camera, controls, sync });
    rigRef.current = { camera, controls, sync };
    const drag = useRef<{ y0: number; dist0: number } | null>(null);

    useCameraBus({
        onDown: (hit) => {
            if (hit.button !== 0) return;
            const { camera: cam, controls: ctl } = rigRef.current;
            drag.current = { y0: hit.pointerPx?.y ?? 0, dist0: cam.position.distanceTo(targetOf(ctl)) };
        },
    });

    useEffect(() => {
        const onMove = (e: PointerEvent): void => {
            const { camera: cam, controls: ctl, sync: doSync } = rigRef.current;
            if (!drag.current) return;
            const d = drag.current.dist0 * Math.exp((e.clientY - drag.current.y0) * 0.006);
            const clamped = Math.min(Math.max(d, 0.2), 5000);
            const dir = cam.position.clone().sub(targetOf(ctl)).normalize();
            if (dir.lengthSq() < 1e-12) return;
            cam.position.copy(targetOf(ctl)).addScaledVector(dir, clamped);
            doSync();
            requestFrame();
        };
        const onUp = (): void => { drag.current = null; };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        return () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };
    }, []);
}

// ============================== ZOOM WINDOW ==============================

/**
 * Zoom Window: drag a screen rectangle, release frames it. The rect corners
 * unproject onto the view plane through the orbit target, then the box is
 * fitted (preview handled by the scope's HTML overlay).
 */
export function useSketchZoomWindowTool() {
    useEscapeToSelect();
    const { camera, controls, gl, unprojectToViewPlane, localPx } = useCameraRig();
    const rigRef = useRef({ camera, controls, gl, unprojectToViewPlane, localPx });
    rigRef.current = { camera, controls, gl, unprojectToViewPlane, localPx };
    const [rect, setRect] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
    const startPx = useRef<THREE.Vector2 | null>(null);

    useCameraBus({
        onDown: (hit) => {
            if (hit.button !== 0 || !hit.pointerPx) return;
            startPx.current = rigRef.current.localPx(hit.pointerPx.x, hit.pointerPx.y);
            setRect(null);
        },
    });

    useEffect(() => {
        const onMove = (e: PointerEvent): void => {
            if (!startPx.current) return;
            const cur = rigRef.current.localPx(e.clientX, e.clientY);
            setRect({
                x0: Math.min(startPx.current.x, cur.x),
                y0: Math.min(startPx.current.y, cur.y),
                x1: Math.max(startPx.current.x, cur.x),
                y1: Math.max(startPx.current.y, cur.y),
            });
        };
        const onUp = (e: PointerEvent): void => {
            if (!startPx.current) return;
            const { camera: cam, controls: ctl, unprojectToViewPlane: unproject } = rigRef.current;
            const cur = rigRef.current.localPx(e.clientX, e.clientY);
            const rectPx = startPx.current;
            startPx.current = null;
            setRect(null);
            const size = cur.clone().sub(rectPx);
            if (Math.abs(size.x) < 4 || Math.abs(size.y) < 4) return;
            const anchor = targetOf(ctl);
            const a = unproject(rectPx, anchor);
            const b = unproject(cur, anchor);
            if (!a || !b) return;
            fitBox(cam, ctl, new THREE.Box3().setFromPoints([a, b]), true);
            requestFrame();
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        return () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };
    }, []);

    return { rect };
}

// ============================== ZOOM EXTENTS ==============================

/** Click (or activate) to frame the whole sketch document. */
export function useSketchZoomExtentsTool(): void {
    useEscapeToSelect();
    const { camera, controls } = useCameraRig();
    const rigRef = useRef({ camera, controls });
    rigRef.current = { camera, controls };

    const frameAll = useCallback((keepDirection: boolean): void => {
        const { camera: cam, controls: ctl } = rigRef.current;
        fitBox(cam, ctl, sketchDocumentBounds(), keepDirection);
        requestFrame();
    }, []);

    useEffect(() => {
        // Sketch frames immediately on activation.
        frameAll(true);
    }, [frameAll]);

    useCameraBus({
        onDown: (hit) => {
            if (hit.button === 0) frameAll(true);
        },
    });
}

// ============================== ZOOM SELECTION ==============================

/**
 * One-shot Zoom Selection (context menu): frames the selection bounds on
 * activation, keeping the current view direction, then hands back to Select.
 * Mirrors Zoom Extents' activate-to-run pattern — the context-menu command
 * just activates this tool id.
 */
export function useSketchZoomSelectionTool(): void {
    const { camera, controls } = useCameraRig();
    const rigRef = useRef({ camera, controls });
    rigRef.current = { camera, controls };

    useEffect(() => {
        const { camera: cam, controls: ctl } = rigRef.current;
        fitBox(cam, ctl, sketchSelectionBounds(), true);
        requestFrame();
        // One shot: the view is framed, Select takes the pointer back.
        import('../../state/store').then(({ useAppStore }) => {
            useAppStore.getState().setActiveTool('select');
        });
    }, []);
}

// ============================== ALIGN VIEW ==============================

/** Armed face target for the one-shot Align View tool (context menu). */
let pendingAlignViewFace: { solidId: string; faceId: string } | null = null;

/** Arm the Align View tool with the face the menu was opened on. */
export function armSketchAlignView(solidId: string, faceId: string): void {
    pendingAlignViewFace = { solidId, faceId };
}

/**
 * One-shot Align View: the camera moves onto the armed face's normal axis
 * through the face center (perpendicular view, current distance kept), then
 * Select resumes. Faces parallel to the ground keep horizon-up; vertical
 * faces keep Z-up.
 */
export function useSketchAlignViewTool(): void {
    useEscapeToSelect();
    const { camera, controls, sync } = useCameraRig();
    const rigRef = useRef({ camera, controls, sync });
    rigRef.current = { camera, controls, sync };

    useEffect(() => {
        const faceRef = pendingAlignViewFace;
        pendingAlignViewFace = null;
        const { camera: cam, controls: ctl, sync: doSync } = rigRef.current;
        if (faceRef) {
            const solid = useSketchStore.getState().solids[faceRef.solidId];
            const face = solid?.faces.find((f) => f.id === faceRef.faceId);
            if (face) {
                const center = new THREE.Vector3(...face.origin);
                const normal = new THREE.Vector3(...face.normal).normalize();
                const distance = Math.max(cam.position.distanceTo(targetOf(ctl)), 1);
                cam.position.copy(center).addScaledVector(normal, distance);
                // Ground-parallel faces have no Z component to keep up with;
                // use the horizon instead, everything else stays Z-up.
                if (Math.abs(normal.z) > 0.999) cam.up.set(0, 1, 0);
                else cam.up.set(0, 0, 1);
                doSync(center);
                requestFrame();
            }
        }
        import('../../state/store').then(({ useAppStore }) => {
            useAppStore.getState().setActiveTool('select');
        });
    }, []);
}

// ============================== POSITION CAMERA ==============================

/** Click places the camera at eye height over the picked point, keeping the heading. */
export function useSketchPositionCameraTool(): void {
    useEscapeToSelect();
    const { camera, controls, sync } = useCameraRig();
    const rigRef = useRef({ camera, controls, sync });
    rigRef.current = { camera, controls, sync };

    useCameraBus({
        onDown: (hit) => {
            if (hit.button !== 0) return;
            const { camera: cam, controls: ctl, sync: doSync } = rigRef.current;
            const eye = hit.point.clone().add(new THREE.Vector3(0, 0, EYE_HEIGHT));
            // Keep the current horizontal heading; if degenerate, look north.
            const dir = targetOf(ctl).clone().sub(cam.position).setZ(0);
            if (dir.lengthSq() < 1e-9) dir.set(0, 1, 0);
            dir.normalize();
            const dist = Math.max(cam.position.distanceTo(targetOf(ctl)), 2);
            const look = eye.clone().addScaledVector(dir, dist);
            cam.position.copy(eye);
            doSync(look);
            requestFrame();
        },
    });
}

// ============================== LOOK AROUND ==============================

/** Drag pivots the view direction around the camera position (head turn). */
export function useSketchLookAroundTool(): void {
    useEscapeToSelect();
    const { camera, controls, sync } = useCameraRig();
    const rigRef = useRef({ camera, controls, sync });
    rigRef.current = { camera, controls, sync };
    const drag = useRef<{ x: number; y: number } | null>(null);

    useCameraBus({
        onDown: (hit) => {
            if (hit.button !== 0 || !hit.pointerPx) return;
            drag.current = { x: hit.pointerPx.x, y: hit.pointerPx.y };
        },
    });

    useEffect(() => {
        const onMove = (e: PointerEvent): void => {
            if (!drag.current) return;
            const { camera: cam, controls: ctl, sync: doSync } = rigRef.current;
            const dx = e.clientX - drag.current.x;
            const dy = e.clientY - drag.current.y;
            drag.current = { x: e.clientX, y: e.clientY };
            const dist = cam.position.distanceTo(targetOf(ctl));
            const dir = cam.position.clone().add(cam.getWorldDirection(new THREE.Vector3()).multiplyScalar(dist));
            const offset = dir.clone().sub(cam.position);
            // Yaw around world Z, pitch around the camera's right axis.
            offset.applyAxisAngle(new THREE.Vector3(0, 0, 1), -dx * 0.005);
            const right = new THREE.Vector3().crossVectors(offset, new THREE.Vector3(0, 0, 1)).normalize();
            offset.applyAxisAngle(right, -dy * 0.005);
            const look = cam.position.clone().add(offset);
            doSync(look);
            requestFrame();
        };
        const onUp = (): void => { drag.current = null; };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        return () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };
    }, []);
}

// ============================== WALK ==============================

/**
 * Drag walks at eye height: vertical movement strides forward/back along
 * the horizontal heading, horizontal movement turns. Collision checking
 * against solids is a v2 step.
 */
export function useSketchWalkTool(): void {
    useEscapeToSelect();
    const { camera, controls, sync } = useCameraRig();
    const rigRef = useRef({ camera, controls, sync });
    rigRef.current = { camera, controls, sync };
    const drag = useRef<{ x: number; y: number } | null>(null);

    useCameraBus({
        onDown: (hit) => {
            if (hit.button !== 0 || !hit.pointerPx) return;
            drag.current = { x: hit.pointerPx.x, y: hit.pointerPx.y };
        },
    });

    useEffect(() => {
        const onMove = (e: PointerEvent): void => {
            if (!drag.current) return;
            const { camera: cam, controls: ctl, sync: doSync } = rigRef.current;
            const dx = e.clientX - drag.current.x;
            const dy = e.clientY - drag.current.y;
            drag.current = { x: e.clientX, y: e.clientY };
            const dist = cam.position.distanceTo(targetOf(ctl));
            // Turn: horizontal drag spins the heading in place.
            const heading = targetOf(ctl).clone().sub(cam.position).setZ(0);
            if (heading.lengthSq() < 1e-9) heading.set(0, 1, 0);
            heading.normalize().applyAxisAngle(new THREE.Vector3(0, 0, 1), -dx * 0.004);
            // Stride: vertical drag walks along the heading (speed scales
            // with the view distance so walking feels consistent at zoom).
            const stride = -dy * Math.max(dist * 0.0015, 0.005);
            const eye = cam.position.clone().addScaledVector(heading, stride);
            eye.z = EYE_HEIGHT;
            const look = eye.clone().addScaledVector(heading, dist);
            cam.position.copy(eye);
            doSync(look);
            requestFrame();
        };
        const onUp = (): void => { drag.current = null; };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        return () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };
    }, []);
}

// ============================== STANDARD VIEWS ==============================

/** The standard view presets the rail's Standard Views group offers. */
export type SketchStandardView =
    | 'iso' | 'top' | 'bottom' | 'front' | 'back' | 'left' | 'right';

/** Camera offset direction per preset, matching the ViewCube's face
    directions (FRONT looks from -Y, RIGHT from +X, ...); iso is the
    SketchUp home corner (ViewCube TOP_FRONT_RIGHT). */
const STANDARD_VIEW_DIRS: Record<SketchStandardView, [number, number, number]> = {
    iso: [1, -1, 1],
    top: [0, 0, 1],
    bottom: [0, 0, -1],
    front: [0, -1, 0],
    back: [0, 1, 0],
    left: [-1, 0, 0],
    right: [1, 0, 0],
};

/**
 * One-shot standard views (ViewCube parity): the camera swings onto the
 * preset's axis through the current orbit target — distance kept — then
 * Select resumes, like the one-shot Align View tool. The shared Z-up
 * camera.up is kept (the controls' orbit axis), so polar views land in
 * the app's canonical lookAt pose.
 */
export function useSketchStandardViewTool(view: SketchStandardView): void {
    const { camera, controls, sync } = useCameraRig();
    const rigRef = useRef({ camera, controls, sync });
    rigRef.current = { camera, controls, sync };

    useEffect(() => {
        const { camera: cam, controls: ctl, sync: doSync } = rigRef.current;
        const center = targetOf(ctl);
        const radius = Math.max(cam.position.distanceTo(center), 1);
        const dir = new THREE.Vector3(...STANDARD_VIEW_DIRS[view]).normalize();
        cam.up.set(0, 0, 1);
        cam.position.copy(center).addScaledVector(dir, radius);
        doSync(center);
        // One shot: the view is posed, Select takes the pointer back.
        import('../../state/store').then(({ useAppStore }) => {
            useAppStore.getState().setActiveTool('select');
        });
    }, [view]);
}
